import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Toaster,
  useWishlistListSelectionDrawer,
} from '@notino/react-styleguide';
import { WishlistList } from '@notino/shared/definitions/types';

import {
  trackCreateNewList,
  TrackCreateNewListArgs,
} from '@containers/WishlistLists/tracking';

import messages from './messages';
import { useWishlistLists } from './useWishlistLists';

type OriginalOnSaveParams = Parameters<
  Parameters<typeof useWishlistListSelectionDrawer>[0]['onSave']
>[0];

type AdditionalOnSaveParams = {
  listPosition?: number;
};

type Props = {
  onSave: (
    params: OriginalOnSaveParams &
      AdditionalOnSaveParams & {
        productIds: string[];
      }
  ) => Promise<void>;
  texts?: Partial<
    Parameters<typeof useWishlistListSelectionDrawer>[0]['texts']
  >;
  interactionSource?: TrackCreateNewListArgs['interaction'];
};

export const useWishlistSelectionDrawer = ({
  texts,
  onSave,
  interactionSource,
}: Props) => {
  const { formatMessage } = useIntl();
  const { createList } = useWishlistLists();
  const wishlistList = React.useRef<WishlistList[] | null>(null);
  const productRef = React.useRef<
    AdditionalOnSaveParams & {
      productName: string;
      productId: string | string[];
    }
  >();

  const { open: _open, close } = useWishlistListSelectionDrawer({
    texts: {
      title: formatMessage(messages.selectListDrawerTitle),
      saveButton: formatMessage(messages.saveButton),
      newListButton: formatMessage(messages.newListButton),
      listNameLabel: formatMessage(messages.listNameLabel),
      createAndSaveButton: formatMessage(messages.createAndSaveButton),
      ...texts,
    },

    onSave: async ({ addedToLists, removedFromLists }) => {
      await onSave({
        addedToLists,
        removedFromLists,
        listPosition: productRef.current.listPosition,
        productIds: Array.isArray(productRef.current.productId)
          ? productRef.current.productId
          : [productRef.current.productId],
      });
      const productName = productRef.current.productName;

      const message = Array.isArray(productRef.current.productId)
        ? getMultipleProductsNotificationMessage({ addedToLists })
        : getSingleProductNotificationMessage({
            addedToLists,
            removedFromLists,
            productName,
          });

      Toaster.info({
        icon: 'IconRegularCheckmark',
        content: (
          <FormattedMessage
            {...message}
            values={{
              ...('values' in message ? message.values : {}),
              b: (chunks) => <b>{chunks}</b>,
            }}
          />
        ),
      });
    },

    onCreateNewList: ({ newListName }) => {
      trackCreateNewList({
        interaction: interactionSource,
        wishlistTotalLists: wishlistList.current?.length + 1,
        wishlistTotalProducts:
          wishlistList.current?.reduce(
            (acc, currentList) => acc + (currentList.products?.length || 0),
            0
          ) + 1,
      });
      return createList({
        name: newListName,
        productIds: Array.isArray(productRef.current.productId)
          ? productRef.current.productId
          : [productRef.current.productId],
      });
    },
  });

  const open = ({
    lists,
    productId,
    selectionMode,
    productName,
    listPosition,
  }: {
    lists: WishlistList[];
    selectionMode?: Parameters<typeof _open>[0]['selectionMode'];
    productId: string | string[];
    productName: string;
  } & AdditionalOnSaveParams) => {
    productRef.current = { productId, productName, listPosition };
    wishlistList.current = lists;
    _open({
      lists: lists.map((list) => ({
        id: list.id,
        name: list.name,
        count: list.products.length,
        isSelected:
          typeof productId === 'string'
            ? list.products.includes(Number(productId))
            : false,
      })),
      selectionMode,
    });
  };

  return { open, close };
};

const getSingleProductNotificationMessage = ({
  addedToLists,
  removedFromLists,
  productName,
}: {
  addedToLists: { name: string }[];
  removedFromLists: { name: string }[];
  productName: string;
}) => {
  if (addedToLists.length > 0 && removedFromLists.length > 0) {
    return messages.listsWereUpdated;
  }
  if (addedToLists.length > 1) {
    return {
      ...messages.productAddedToLists,
      values: { product: productName },
    };
  }
  if (addedToLists.length === 1) {
    return {
      ...messages.productAddedToList,
      values: {
        product: productName,
        list: addedToLists[0].name,
      },
    };
  }
  if (removedFromLists.length > 1) {
    return {
      ...messages.productRemovedFromLists,
      values: {
        product: productName,
      },
    };
  }
  if (removedFromLists.length === 1) {
    return {
      ...messages.productRemovedFromList,
      values: {
        product: productName,
        list: removedFromLists[0].name,
      },
    };
  }
  return messages.listsWereUpdated; // This should never happen
};

const getMultipleProductsNotificationMessage = ({
  addedToLists,
}: {
  addedToLists: { name: string }[];
}) => {
  if (addedToLists.length === 1) {
    return {
      ...messages.productsMovedToList,
      values: {
        list: addedToLists[0].name,
      },
    } as const;
  }
  return messages.productsMovedToLists;
};
